import React from 'react';
import FormModal from "../../../components/common/modals/FormModal";
import graphql from "babel-plugin-relay/macro";
import {useFragment, useMutation} from "react-relay";
import {allEthnicitiesDropdown, allGendersDropdown, allProfessionalDesignationDropdown} from "../../../relay/Fragments";
import {createOptions, yesNoUnknownOptions} from "../../../helpers/utils";
import dayjs from "dayjs";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {toast} from "react-toastify";
import HorizontalFormInput from "../../../components/common/horizontal-form-inputs/HorizontalFormInput";
import HorizontalFormDate from "../../../components/common/horizontal-form-inputs/HorizontalFormDate";
import HorizontalFormCheck from "../../../components/common/horizontal-form-inputs/HorizontalFormCheck";
import HorizontalFormSelect from "../../../components/common/horizontal-form-inputs/HorizontalFormSelect";
import {bandedSchema} from "../../../helpers/bandedUtils";

const EditPractitionerVersionModal = ({showModal, onClose, selectedVersion, genders, ethnicities, professionalDesignations}) => {
    const mutation = graphql`
    mutation EditPractitionerVersionModalMutation($id: ID!, $firstName: String!, $lastName: String!, $middleInitial: String!, 
    $nickName: String!, $nameSuffix: String!, $dateOfBirth: DateInput, $allowedToBePcp: Boolean, $qualification: String!,
        $genderId: StringInput, $ethnicityId: StringInput, $professionalDesignationId: StringInput,
        $withLimitedEnglish: YesNoUnknown, $seeMinorities: YesNoUnknown, $withDisabilities: YesNoUnknown, $withDiverseSexualOrientations: YesNoUnknown,
        $withDiverseGenderIdentities: YesNoUnknown, $livingInRuralAreas: YesNoUnknown, $adverselyAffectedByPovertyOrInequality: YesNoUnknown,
        $credentialingDelegated: Boolean!, $effectiveFrom: DateOnly!, $effectiveTo: DateOnly!) {
        practitioner {
            modifyVersion(input: {practitionerVersionId: $id, firstName: $firstName, lastName: $lastName, middleInitial: $middleInitial, 
            nameSuffix: $nameSuffix, nickname: $nickName, qualification: $qualification, dateOfBirth: $dateOfBirth, allowedToBePcp: $allowedToBePcp,
            genderId: $genderId, ethnicityId: $ethnicityId, professionalDesignationId: $professionalDesignationId, effectiveFrom: $effectiveFrom,
            effectiveTo: $effectiveTo, willingToSeePeopleWithLimitedEnglish: $withLimitedEnglish, willingToSeeMinorities: $seeMinorities,
            willingToSeePeopleWithDisabilities: $withDisabilities, willingToSeePeopleWithDiverseSexualOrientations: $withDiverseSexualOrientations,
            willingToSeePeopleWithDiverseGenderIdentities: $withDiverseGenderIdentities, willingToSeePeopleLivingInRuralAreas: $livingInRuralAreas,
            willingToSeePeopleAdverselyAffectedByPovertyOrInequality: $adverselyAffectedByPovertyOrInequality, credentialingDelegated: $credentialingDelegated}) {
                practitionerVersion {
                    id
                    practitioner {
                      versions {
                          ...PractitionerVersions_Versions
                      }
                        applicableVersion {
                            id
                        }}}}}}`
    const allGenders = createOptions(useFragment(allGendersDropdown, genders), "id","name")
    const allEthnicities = createOptions(useFragment(allEthnicitiesDropdown, ethnicities),"id", "name");
    const allProfessionalDesignations = createOptions(useFragment(allProfessionalDesignationDropdown, professionalDesignations), "id", "name");
    const [commitMutation, isInFlight] = useMutation(mutation);
    const effectiveFrom = selectedVersion?.effectiveFrom ? dayjs(selectedVersion?.effectiveFrom).toDate() : '';
    const effectiveTo = selectedVersion?.effectiveTo ? dayjs(selectedVersion?.effectiveTo).toDate() : '';
    const dateOfBirth = selectedVersion?.dateOfBirth ? dayjs(selectedVersion?.dateOfBirth).toDate() : '';
    const defaultValues = {
        id: selectedVersion?.id,
        firstName: selectedVersion?.firstName,
        lastName: selectedVersion?.lastName,
        middleInitial: selectedVersion?.middleInitial,
        nickName: selectedVersion?.nickName,
        nameSuffix: selectedVersion?.nameSuffix,
        allowedToBePcp: selectedVersion?.allowedToBePcp,
        qualification: selectedVersion?.qualification,
        genderId: selectedVersion?.gender?.id ?? null,
        ethnicityId: selectedVersion?.ethnicity?.id ?? null,
        professionalDesignationId: selectedVersion?.professionalDesignation?.id ?? null,
        dateOfBirth: dateOfBirth,
        withLimitedEnglish: selectedVersion?.willingToSeePeopleWithLimitedEnglish,
        seeMinorities: selectedVersion?.willingToSeeMinorities,
        withDisabilities: selectedVersion?.willingToSeePeopleWithDisabilities,
        withDiverseSexualOrientations: selectedVersion?.willingToSeePeopleWithDiverseSexualOrientations,
        withDiverseGenderIdentities: selectedVersion?.willingToSeePeopleWithDiverseGenderIdentities,
        livingInRuralAreas: selectedVersion?.willingToSeePeopleLivingInRuralAreas,
        adverselyAffectedByPovertyOrInequality: selectedVersion?.willingToSeePeopleAdverselyAffectedByPovertyOrInequality,
        credentialingDelegated: selectedVersion?.credentialingDelegated,
        effectiveFrom: effectiveFrom,
        effectiveTo: effectiveTo
    }
    const schema = bandedSchema.shape({
        firstName: yup.string().required("Language is required"),
        lastName: yup.string().required("Language is required"),
        middleInitial: yup.string().max(1, "Middle Initial can only be 1 letter"),
        dateOfBirth: yup.date().nullable().transform((curr, orig) => orig === '' ? null : curr)
    });
    const methods = useForm({values: defaultValues, resolver: yupResolver(schema)});
    const handleSubmit = (data) => {
        commitMutation({variables: {...data}, onCompleted: (data) => {
                if(data.practitioner) {
                    toast.success("Version for Provider updated")
                    methods.reset();
                    onClose();
                }
            }})
    }
    return (
        <FormModal showModal={showModal}
                   onClose={onClose}
                   title={"Update Version"}
                   methods={methods}
                   isInFlight={isInFlight}
                   onSubmit={handleSubmit}
        >
            <HorizontalFormInput controlId={"firstName"} label={"First Name"} />
            <HorizontalFormInput controlId={"middleInitial"} label={"Middle Initial"} />
            <HorizontalFormInput controlId={"lastName"} label={"Last Name"} />
            <HorizontalFormInput controlId={"nameSuffix"} label={"Name Suffix"} />
            <HorizontalFormInput controlId={"nickName"} label={"Nick Name"} />
            <HorizontalFormInput controlId={"qualification"} label={"Qualification"} />
            <HorizontalFormSelect options={allProfessionalDesignations} controlId={"professionalDesignationId"} label={"Professional Designation"} />
            <HorizontalFormSelect options={allGenders} controlId={"genderId"} label={"Gender"} />
            <HorizontalFormSelect options={allEthnicities} controlId={"ethnicityId"} label={"Ethnicity"} />
            <HorizontalFormDate controlId={"dateOfBirth"} label={"Date of Birth"} />
            <HorizontalFormCheck controlId={"allowedToBePcp"} label={"Allowed to Be Pcp"} />
            <HorizontalFormCheck controlId={"credentialingDelegated"} label={"Credentialing Delegated"} />
            <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"withLimitedEnglish"} label={"Willing to see people with limited English"} />
            <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"seeMinorities"} label={"Willing to see minorities"} />
            <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"withDisabilities"} label={"Willing to see people with disabilities"} />
            <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"withDiverseSexualOrientations"} label={"Willing to see people with diverse sexual orientations"} />
            <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"withDiverseGenderIdentities"} label={"Willing to see people with diverse gender identities"} />
            <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"livingInRuralAreas"} label={"Willing to see people living in rural areas"} />
            <HorizontalFormSelect options={yesNoUnknownOptions} controlId={"adverselyAffectedByPovertyOrInequality"} label={"Willing to see people adversely affected by poverty or inequality"} />
            <HorizontalFormDate controlId={"effectiveFrom"} label={"Effective From"} />
            <HorizontalFormDate controlId={"effectiveTo"} label={"Effective To"} />
        </FormModal>
    )
}
export default EditPractitionerVersionModal